/**
 * Stripe.js loader for the in-app Payment Element (LF-4605).
 *
 * `loadStripe` injects Stripe's script and must run once per page load, so the
 * promise is memoised at module scope. The publishable key is a public,
 * browser-safe value (`pk_test_…` / `pk_live_…`) provided via
 * `NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY`; it is NOT a secret. When the key is
 * absent we return null rather than throwing, so the billing tab still renders
 * (the card surface shows a "billing temporarily unavailable" state instead of
 * a crash).
 */

'use client';

import { loadStripe, type Stripe } from '@stripe/stripe-js';

let stripePromise: Promise<Stripe | null> | null = null;

export function getStripe(): Promise<Stripe | null> {
  if (stripePromise) return stripePromise;

  const publishableKey = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY;
  if (!publishableKey) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        '[billing] NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY is not set, so the in-app Payment Element cannot load.'
      );
    }
    stripePromise = Promise.resolve(null);
    return stripePromise;
  }

  stripePromise = loadStripe(publishableKey);
  return stripePromise;
}

/** Whether a publishable key is configured (drives the card surface's fallback copy). */
export function isStripeConfigured(): boolean {
  return Boolean(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY);
}
